import React from "react";
import { Text, Icon, Input, Panel } from "@clearabee/ui-library-native";
import { Platform, TouchableOpacity, View } from "react-native";
import { ids, styles } from "./WasteDescriptionTooltip.styles";
import { useTranslation } from "react-i18next";
import { Anchor } from "components/Core/Anchor/Anchor";
import { mergeList, RedirectItem } from "lib/helpers/tooltipHelper";

interface TooltipOverlayProps {
  children: React.ReactNode;
}

interface TooltipProps {
  onClose: () => void;
  onDisable: () => void;
  isDisabled: boolean;
  surchargeItems: string[];
  detectedMessages: string[];
  prohibitedItems: string[];
  redirectItems: RedirectItem[];
}

export const TooltipOverlay =
  Platform.OS === "web"
    ? ({ children }: TooltipOverlayProps) => (
        <div
          style={{
            zIndex: 1,
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "fixed",
          }}
        >
          {children}
        </div>
      )
    : React.Fragment;

export const WasteDescriptionTooltip = ({
  onClose,
  onDisable,
  isDisabled,
  surchargeItems,
  detectedMessages,
  prohibitedItems,
  redirectItems,
}: TooltipProps): React.ReactElement | null => {
  const [translate, i18n] = useTranslation();

  const numberOfMessages =
    +!!surchargeItems.length +
    +!!prohibitedItems.length +
    +!!redirectItems.length +
    detectedMessages.length;

  if (!numberOfMessages || isDisabled) {
    return null;
  }

  return (
    <Panel styles={styles.container}>
      <View style={Platform.OS === "web" && styles.innerContainer}>
        <View>
          {!!surchargeItems.length && (
            <Text styles={styles.tooltipMessage}>
              {numberOfMessages > 1 && <Text>{`\u2022`} </Text>}

              {translate("hwa.screens.productDetails.extrasTooltip")}
            </Text>
          )}
          {!!prohibitedItems.length && (
            <Text styles={styles.tooltipMessage}>
              {numberOfMessages > 1 && <Text>{`\u2022`} </Text>}

              {translate("hwa.screens.productDetails.prohibitedItemsTooltip")}
            </Text>
          )}
          {!!redirectItems.length && (
            <Text styles={styles.tooltipMessage}>
              {numberOfMessages > 1 && <Text>{`\u2022`} </Text>}

              {translate("hwa.screens.productDetails.redirectItemsTooltip")}
              {redirectItems.map(({ service, keywords }, index) => (
                <React.Fragment key={`${service}_${index}`}>
                  {!!index && ", "}
                  <Anchor
                    routeName="ServicePage"
                    params={{ categorySku: service }}
                    newTab
                  >
                    <Text
                      styles={{
                        textDecorationLine: "underline",
                      }}
                      color="dark"
                    >
                      {translate(
                        "hwa.screens.productDetails.redirectItemsTooltipEach",
                        {
                          service,
                          keywords: mergeList(keywords),
                          interpolation: {
                            escapeValue: false,
                          },
                        },
                      )}
                    </Text>
                  </Anchor>
                </React.Fragment>
              ))}
            </Text>
          )}
          {detectedMessages.map((message) => (
            <Text styles={styles.tooltipMessage} key={message}>
              <>
                <Text>{`\u2022`} </Text>

                {i18n.exists(message) ? translate(message) : message}
              </>
            </Text>
          ))}
          <View
            style={styles.doNotShowPrompt}
            dataSet={{ media: ids.doNotShowPrompt }}
          >
            <Input.Checkbox
              name="disableTooltip"
              label={translate("hwa.screens.productDetails.doNotShowPrompt")}
              value={isDisabled}
              onChange={onDisable}
            />
          </View>
        </View>
      </View>
      <TouchableOpacity onPress={onClose} style={{ alignSelf: "flex-start" }}>
        <Icon.Close size="small" color="dark" />
      </TouchableOpacity>
    </Panel>
  );
};
